<template>
  <v-main class="offer-main">
    <v-card
      class="ma-1 offer-card"
      flat
    >
      <v-card-title v-if="offer">
        {{ offer.title || offerTitle }}
      </v-card-title>
      <v-card-subtitle v-if="offer && offer.applied_at">
        Редакция от {{ offer.applied_at | dateformat(false) }}
      </v-card-subtitle>
      <v-container
        fluid
        class="pa-2 offer-container"
      >
        <div
          v-if="offer"
          class="mb-2 offer-controls"
        >
          <div class="d-flex flex-wrap align-center mt-0">
            <v-checkbox
              v-model="consentChecked"
              :disabled="consentDisabled"
              :label="consentLabel"
              hide-details
              class="mt-0 mr-2"
              dense
            />
            <v-spacer />
            <v-btn
              v-if="!offer.accepted"
              color="primary"
              :disabled="!canAccept || submitting"
              :loading="submitting"
              class="mr-2"
              @click="acceptOffer"
            >
              Принять
            </v-btn>
            <v-btn
              text
              @click="closeView"
            >
              Закрыть
            </v-btn>
          </div>
        </div>

        <v-alert
          v-if="error"
          type="error"
          dense
          outlined
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <div
          v-if="loading"
          class="text-center py-6"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>

        <div
          v-else
          class="offer-viewer-wrap"
        >
          <div class="offer-viewer-topbar">
            <span
              v-if="offer && !offer.accepted"
              class="caption grey--text offer-hint"
            >
              Прокрутите документ до конца, чтобы подтвердить ознакомление с офертой.
            </span>
            <div
              class="offer-viewer-zoom"
              @click.stop
            >
              <v-btn
                icon
                small
                @click="zoomOut"
              >
                <v-icon small>mdi-magnify-minus</v-icon>
              </v-btn>
              <span class="caption grey--text mx-1">{{ Math.round(pdfScale * 100) }}%</span>
              <v-btn
                icon
                small
                @click="zoomIn"
              >
                <v-icon small>mdi-magnify-plus</v-icon>
              </v-btn>
            </div>
          </div>
          <div
            ref="scroll"
            class="offer-viewer"
          >
            <div
              ref="pages"
              class="offer-pages"
            />
          </div>
        </div>
      </v-container>
    </v-card>
  </v-main>
</template>

<script>
import { mapGetters } from "vuex";
import offers from "@/api/offers";

export default {
  name: "OfferView",
  data() {
    return {
      offer: null,
      loading: true,
      error: "",
      consentChecked: false,
      scrolledEnough: false,
      maxScrollRatio: 0,
      submitting: false,
      pdfReady: false,
      pdfScale: 1,
      pdfDoc: null,
      baseScale: 1,
      pdfRendering: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["needToFulfillInfo"]),
    offerTitle() {
      return "Публичная оферта";
    },
    consentLabel() {
      return "Я ознакомлен(а) с текстом Оферты и принимаю её условия.";
    },
    transitionLabel() {
      return "Согласен(а) на переход со старого договора";
    },
    consentDisabled() {
      if (!this.offer) {
        return true;
      }
      if (this.offer.accepted) {
        return true;
      }
      return !this.scrolledEnough;
    },
    canAccept() {
      if (this.needToFulfillInfo) {
        return false;
      }
      if (!this.offer) {
        return false;
      }
      if (this.offer.accepted) {
        return true;
      }
      if (!this.consentChecked) {
        return false;
      }
      return true;
    },
  },
  created() {
    this.loadOffer();
  },
  mounted() {
    this.ensurePdfLib();
  },
  beforeDestroy() {
    if (this.$refs.scroll) {
      this.$refs.scroll.removeEventListener("scroll", this.onScroll);
    }
  },
  methods: {
    loadOffer() {
      this.loading = true;
      this.error = "";
      offers
        .getCurrent()
        .then((response) => {
          this.offer = response.data;
          if (this.offer && this.offer.accepted) {
            this.scrolledEnough = true;
            this.consentChecked = true;
          }
          this.loading = false;
          if (this.pdfReady) {
            this.$nextTick(() => this.renderPdf());
          }
        })
        .catch(() => {
          this.error = "Оферта недоступна для вашей роли.";
          this.loading = false;
        });
    },
    ensurePdfLib() {
      if (window.pdfjsLib) {
        this.pdfReady = true;
        if (this.offer && !this.loading) {
          this.$nextTick(() => this.renderPdf());
        }
        return;
      }
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      script.onload = () => {
        this.pdfReady = true;
        if (this.offer && !this.loading) {
          this.$nextTick(() => this.renderPdf());
        }
      };
      script.onerror = () => {
        this.error = "Не удалось загрузить просмотрщик PDF.";
      };
      document.body.appendChild(script);
    },
    onScroll() {
      const el = this.$refs.scroll;
      if (!el) {
        return;
      }
      const max = el.scrollHeight - el.clientHeight;
      const current = el.scrollTop;
      const ratio = max <= 0 ? 1 : current / max;
      if (ratio > this.maxScrollRatio) {
        this.maxScrollRatio = ratio;
      }
      if (!this.offer || this.offer.accepted) {
        this.scrolledEnough = true;
        return;
      }
      if (this.maxScrollRatio >= 0.97) {
        this.scrolledEnough = true;
      }
    },
    async renderPdf() {
      if (!this.offer || !this.offer.file_url || !window.pdfjsLib) {
        return;
      }
      const container = this.$refs.pages;
      const scroll = this.$refs.scroll;
      if (!container || !scroll) {
        return;
      }
      container.innerHTML = "";
      scroll.removeEventListener("scroll", this.onScroll);
      scroll.addEventListener("scroll", this.onScroll, { passive: true });

      const pdfjsLib = window.pdfjsLib;
      if (pdfjsLib.GlobalWorkerOptions) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      }

      let pdf;
      try {
        const loadingTask = pdfjsLib.getDocument(this.offer.file_url);
        pdf = await loadingTask.promise;
      } catch (e) {
        this.error = "Не удалось загрузить документ оферты.";
        return;
      }

      try {
        this.pdfDoc = pdf;
        const page1 = await pdf.getPage(1);
        const vp1 = page1.getViewport({ scale: 1 });
        const paddingPx = 32;
        const fitWidth = Math.max(0, scroll.clientWidth - paddingPx);
        if (fitWidth > 0 && vp1.width > 0) {
          this.baseScale = Math.min(1.5, Math.max(0.3, fitWidth / vp1.width));
        } else {
          this.baseScale = 1;
        }
        await this.renderPdfPages();
      } catch (e) {
        this.error = "Не удалось отобразить документ оферты.";
      }
    },
    async renderPdfPages() {
      const container = this.$refs.pages;
      const scroll = this.$refs.scroll;
      if (!container || !scroll || !this.pdfDoc) {
        return;
      }
      if (this.pdfRendering) {
        return;
      }
      this.pdfRendering = true;
      const scale = this.baseScale * this.pdfScale;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 3);
      const pdf = this.pdfDoc;
      container.innerHTML = "";
      try {
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          const page = await pdf.getPage(pageNumber);
          const vp = page.getViewport({ scale: scale * pixelRatio });

          const pageWrap = document.createElement("div");
          pageWrap.className = "offer-page";

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = vp.width;
          canvas.height = vp.height;
          canvas.style.width = `${vp.width / pixelRatio}px`;
          canvas.style.height = `${vp.height / pixelRatio}px`;
          pageWrap.appendChild(canvas);
          container.appendChild(pageWrap);

          await page.render({
            canvasContext: context,
            viewport: vp,
          }).promise;
        }
      } catch (e) {
        this.error = "Не удалось отобразить документ оферты.";
      } finally {
        this.pdfRendering = false;
        if (this.pdfDoc && (this.baseScale * this.pdfScale) !== scale) {
          this.$nextTick(() => this.renderPdfPages());
        }
      }
    },
    zoomIn() {
      this.pdfScale = Math.min(2, this.pdfScale + 0.25);
      this.renderPdfPages();
    },
    zoomOut() {
      this.pdfScale = Math.max(0.5, this.pdfScale - 0.25);
      this.renderPdfPages();
    },
    async acceptOffer() {
      if (this.needToFulfillInfo) {
        this.$store.dispatch("alertError", "Пожалуйста, заполните информацию о компании для принятия оферты.");
        return;
      }
      if (!this.offer) {
        return;
      }
      if (this.offer.accepted) {
        this.closeView();
        return;
      }
      this.submitting = true;
      const body = {
        offer_id: this.offer.id,
        max_scroll_ratio: this.maxScrollRatio || 0,
        consent_result: "accepted",
        consent_text: this.consentLabel,
      };
      if (this.offer.requires_legacy_transition) {
        body.transition_text = this.transitionLabel;
      }
      try {
        await offers.accept(body);
        this.closeView();
      } catch (e) {
        this.$store.dispatch("alertError", "Не удалось принять оферту.");
      } finally {
        this.submitting = false;
      }
    },
    closeView() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: "StocksView" });
      }
    },
  },
};
</script>

<style scoped>
.offer-card {
  margin-bottom: 2px !important;
}

.offer-container {
  padding-bottom: 2px !important;
}

.offer-viewer-wrap {
  position: relative;
  height: calc(100vh - 200px);
  min-height: 500px;
}

.offer-viewer-topbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  min-height: 36px;
  background: transparent;
  pointer-events: none;
}

.offer-viewer-topbar .offer-viewer-zoom {
  pointer-events: auto;
}

.offer-hint {
  background: transparent;
  pointer-events: none;
}

.offer-viewer-zoom {
  display: flex;
  align-items: center;
  padding: 2px 6px;
  margin-left: auto;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.offer-viewer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-x: auto;
  overflow-y: auto;
  padding: 12px 16px;
  padding-top: 44px;
  padding-bottom: 8px;
  background-color: #f5f5f5;
  max-width: 100%;
  box-sizing: border-box;
}

.offer-pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  min-width: 0;
}

.offer-page {
  margin: 0 auto 16px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  max-width: 100%;
  min-width: 0;
}

.offer-page canvas {
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
}

@media (max-width: 600px) {
  .offer-main .offer-card {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .offer-container {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
  .offer-viewer {
    padding-left: 8px;
    padding-right: 8px;
  }
  .offer-viewer-topbar {
    flex-wrap: wrap;
    padding: 6px 8px;
  }
  .offer-viewer-topbar .offer-hint {
    order: 1;
    width: 100%;
    margin-bottom: 4px;
  }
  .offer-viewer-wrap {
    height: calc(100vh - 180px);
    min-height: 320px;
  }
}
</style>
